import type { Hex, Address } from 'viem';

export enum OrderType {
  Market = 'market',
  Limit  = 'limit',
  Stop   = 'stop',
}

export enum CancelOrderType {
  Limit        = 'limit',
  PendingOpen  = 'pendingOpen',
  PendingClose = 'pendingClose',
}

export interface OpenTradeParams {
  /**
   * Trading pair id — same value as `Pair.pairId` returned by `getPairs()`.
   * Accepts both the numeric string (e.g. `"0"`) and a plain `number`.
   */
  pairId: string | number;
  /** true = long, false = short. */
  buy: boolean;
  /** Entry price as a decimal string (e.g. "65000.50"). */
  price: string;
  /** Collateral in USD as a decimal string (e.g. "100.00"). Fixed minimum is `MIN_OPEN_SIZE_USD` ($5). */
  collateral: string;
  /** Leverage as a decimal string (e.g. "10" for 10×). Contract-side limits apply. */
  leverage: string;
  /** Order type. */
  type: OrderType;
  /** Take profit price as a decimal string. Optional. */
  takeProfit?: string;
  /** Stop loss price as a decimal string. Optional. */
  stopLoss?: string;
  /** Per-trade slippage override in basis points. Overrides client default. */
  slippage?: number;
  /**
   * Mark the trade as a day trade. Must be `true` when the desired leverage
   * exceeds `pair.overnightMaxLeverage` (and `overnightMaxLeverage > 0`).
   * Day trades are automatically closed before market close if not manually
   * closed beforehand. Defaults to `false`.
   */
  isDayTrade?: boolean;
  /**
   * Per-trade builder fee override. Each field falls back to the client-level
   * `builder` config when omitted. Pass `feeBps: 0` to open without a builder fee.
   */
  builder?: {
    address?: Address;
    feeBps?: number;
  };
}

export interface CloseTradeParams {
  /**
   * Trading pair id — same value as `Position.pairId` returned by `getOpenPositions`.
   * Accepts both the numeric string (e.g. `"0"`) and a plain `number` for ergonomics.
   */
  pairId: string | number;
  /**
   * Position index within the pair — same value as `Position.idx` returned by
   * `getOpenPositions`. Identifies which of the trader's open positions to close.
   */
  idx: number;
  /** Current market price as a decimal string. */
  price: string;
  /** Percentage to close (1–100). */
  closePercent: number;
  /** Per-trade slippage override in basis points. */
  slippage?: number;
  /**
   * When true, verifies USDC allowance covers the oracle fee before submitting.
   * Skip by default if you know allowance is sufficient.
   */
  checkAllowance?: boolean;
}

/**
 * Cancel a pending limit order.
 */
export interface CancelLimitOrderParams {
  type: CancelOrderType.Limit;
  /**
   * Trading pair id — same value as `OpenOrder.pairId` returned by `getOpenOrders`.
   * Accepts both the numeric string (e.g. `"0"`) and a plain `number`.
   */
  pairId: string | number;
  /**
   * Limit order index within the pair — same value as `OpenOrder.idx` returned
   * by `getOpenOrders`. Identifies which limit order to cancel.
   */
  idx: number;
}

/**
 * Cancel a timed-out market open order.
 */
export interface CancelPendingOpenParams {
  type: CancelOrderType.PendingOpen;
  /** Market order ID from the original openTrade submission. */
  orderId: number;
}

/**
 * Cancel a timed-out market close order.
 */
export interface CancelPendingCloseParams {
  type: CancelOrderType.PendingClose;
  /** Market order ID from the original closeTrade submission. */
  orderId: number;
  /**
   * If true, re-submits the close order after cancelling the timed-out one.
   * Defaults to false (cancel only, trade remains open).
   */
  retry?: boolean;
}

export type CancelOrderParams =
  | CancelLimitOrderParams
  | CancelPendingOpenParams
  | CancelPendingCloseParams;

export interface ModifyOrderParams {
  /**
   * Trading pair id.
   * - Updating TP/SL on an open position → use `Position.pairId` from `getOpenPositions`.
   * - Updating a limit order's trigger price → use `OpenOrder.pairId` from `getOpenOrders`.
   * Accepts both the numeric string (e.g. `"0"`) and a plain `number`.
   */
  pairId: string | number;
  /**
   * Slot index within the pair.
   * - Updating TP/SL on an open position → use `Position.idx`.
   * - Updating a limit order → use `OpenOrder.idx`.
   */
  idx: number;
  /** New limit-order trigger price. Required when modifying a limit order's price. */
  price?: string;
  /** New take profit price. */
  takeProfit?: string;
  /** New stop loss price. */
  stopLoss?: string;
}

export interface UpdateCollateralParams {
  /**
   * Trading pair id — same value as `Position.pairId` returned by `getOpenPositions`.
   * Accepts both the numeric string (e.g. `"0"`) and a plain `number`.
   */
  pairId: string | number;
  /**
   * Position index within the pair — same value as `Position.idx` returned by
   * `getOpenPositions`. Identifies which position to top up or partially withdraw from.
   */
  idx: number;
  /**
   * Amount in USD as a decimal string.
   * Positive → add collateral (topUpCollateral).
   * Negative → remove collateral (removeCollateral).
   */
  amount: string;
}

export interface Balances {
  /** USDC balance as a decimal string (e.g. `"1234.567890"`). */
  usdc: string;
  /** ETH balance as a decimal string (e.g. `"0.001234567890123456789"`). */
  eth: string;
  /** USDC allowance granted to the TradingStorage contract as a decimal string. Max approval is preserved exactly. */
  allowance: string;
}

export interface AllowanceStatus {
  /** Current USDC allowance (6 decimals, raw bigint). */
  current: bigint;
  /** Required USDC allowance (6 decimals, raw bigint). */
  required: bigint;
  /** true if current >= required. */
  sufficient: boolean;
}

export interface SubmissionResult {
  txHash: Hex;
  /** Set for gasless submissions — the Safe smart account address that sent the UserOp. */
  smartAccountAddress?: Address;
}

export interface BuiltSafeTxCall {
  to: Address;
  data: Hex;
  value: bigint;
}

export interface BuiltEoaTxRequest {
  kind: 'eoa';
  to: Address;
  data: Hex;
  value: bigint;
  from: Address;
  traderAddress: Address;
}

export interface BuiltSafeTxRequest {
  kind: 'safe';
  safeAddress: Address;
  traderAddress: Address;
  calls: [BuiltSafeTxCall];
}

export type BuiltTxRequest = BuiltEoaTxRequest | BuiltSafeTxRequest;

/** Internal encoded transaction before signer.prepare(). */
export interface EncodedTx {
  to: Address;
  data: Hex;
  value: bigint;
}

// ─────────────────────────────────────────────────────────────────────────────
// Onboarding
// ─────────────────────────────────────────────────────────────────────────────

/** A single outstanding onboarding step, with the transaction that clears it. */
export interface OnboardingStep {
  kind: 'approveUsdc' | 'setDelegate' | 'setupGaslessDelegation';
  /** Human-readable description of what this step does. */
  description: string;
  /**
   * The transaction to submit. Always sent from the trader's own account —
   * a delegate cannot approve USDC or register itself on the trader's behalf.
   */
  tx: BuiltTxRequest;
}

/**
 * What still stands between a trader and their first trade.
 *
 * ```ts
 * const status = await client.getOnboardingStatus();
 * if (!status.ready) {
 *   for (const step of status.steps) {
 *     console.log(step.description);   // render a checklist
 *     await wallet.sendTransaction(step.tx);
 *   }
 * }
 * ```
 */
export interface OnboardingStatus {
  /** `true` when nothing is outstanding and the trader can actually open a position. */
  ready: boolean;
  /** USDC allowance to TradingStorage is below `requiredUsd`. */
  needsApproval: boolean;
  /**
   * USDC balance is below `requiredUsd`. No transaction can fix this — the
   * trader has to fund the account — so it produces no step, but it does keep
   * `ready` false.
   */
  needsFunding: boolean;
  /** No delegate registered, or a different one than this client expects. */
  needsDelegate: boolean;
  /** Self + Gasless only — the smart account has not been authorised yet. */
  needsGaslessSetup: boolean;
  /** Outstanding steps, in the order they should be submitted. */
  steps: OnboardingStep[];
  /** Current USDC allowance to TradingStorage, as a decimal string. */
  allowance: string;
  /** Trader's USDC balance, as a decimal string. */
  usdcBalance: string;
  /** Trader's ETH balance, as a decimal string. Needed for gas in self-submit modes. */
  ethBalance: string;
  /** Delegate currently registered on-chain, or `undefined` when none is set. */
  currentDelegate?: Address;
}
